package info.rezident.app.core

import kotlin.js.json

object AppTracking {

    // Event names
    const val EVENT_LANDING_CTA_CLICK = "landing_cta_click"
    const val EVENT_LANDING_CONNECT_CLICK = "landing_connect_click"
    const val EVENT_LANDING_FAQ_CLICK = "landing_faq_click"
    const val EVENT_LANDING_INSTAGRAM_CLICK = "landing_instagram_click"
    const val EVENT_LANDING_CONTACT_INSTAGRAM_CLICK = "landing_contact_instagram_click"
    const val EVENT_LANDING_CONTACT_EMAIL_CLICK = "landing_contact_email_click"
    const val EVENT_NAV_HOME_CLICK = "nav_home_click"
    const val EVENT_NAV_HOW_IT_WORKS_CLICK = "nav_how_it_works_click"
    const val EVENT_NAV_CONTACT_CLICK = "nav_contact_click"
    const val EVENT_CONTACT_FORM_SENT = "contact_form_sent"
    const val EVENT_QUIZ_STARTED = "quiz_started"
    const val EVENT_QUIZ_QUESTION_ANSWERED = "quiz_question_answered"
    const val EVENT_QUIZ_COMPLETED = "quiz_completed"
    const val EVENT_PAYWALL_OFFER_VIEW = "paywall_offer_view"
    const val EVENT_PAYWALL_OFFER_ACCEPT = "paywall_offer_accept"
    const val EVENT_PAYWALL_OFFER_SKIP = "paywall_offer_skip"
    const val EVENT_PAYWALL_VIEW = "paywall_view"
    const val EVENT_PAYWALL_PLAN_CLICK = "paywall_plan_click"
    const val EVENT_SIGN_UP = "sign_up"
    const val EVENT_LOGIN = "login"
    const val EVENT_WAITLIST_INSTAGRAM_CLICK = "waitlist_instagram_click"
    const val EVENT_ERROR_DISPLAYED = "error_displayed"
    const val EVENT_QUIZ_RECOMMENDATIONS_CLICK = "quiz_recommendations_click"
    const val EVENT_RECOMMENDATIONS_PAGE_OPEN = "recommendations_page_open"
    const val EVENT_RECOMMENDATIONS_PAYWALL_VIEW = "recommendations_paywall_view"
    const val EVENT_RECOMMENDATIONS_PAYWALL_COMPACT_CLICK = "recommendations_paywall_compact_click"
    const val EVENT_RECOMMENDATIONS_PROS_CONS_GENERATE = "recommendations_pros_cons_generate"
    const val EVENT_RECOMMENDATIONS_PROS_CONS_RESULT = "recommendations_pros_cons_result"
    const val EVENT_RECOMMENDATIONS_GENERATE_START = "recommendations_generate_start"
    const val EVENT_RECOMMENDATIONS_GENERATE_RESULT = "recommendations_generate_result"
    const val EVENT_RECOMMENDATIONS_DETAILS_CLICK = "recommendations_details_click"
    const val EVENT_RECOMMENDATIONS_UNLOCK_CLICK = "recommendations_unlock_click"
    const val EVENT_RECOMMENDATIONS_LOCKED_CARD_CLICK = "recommendations_locked_card_click"
    const val EVENT_RECOMMENDATIONS_STICKY_CTA_CLICK = "recommendations_sticky_cta_click"
    const val EVENT_RECOMMENDATIONS_AI_RETRY_CLICK = "recommendations_ai_retry_click"
    const val EVENT_RECOMMENDATIONS_ACCOUNT_CLICK = "recommendations_account_click"
    const val EVENT_RECOMMENDATIONS_ANSWERS_CLICK = "recommendations_answers_click"
    const val EVENT_RECOMMENDATIONS_DISCOUNT_CODE_CLICK = "recommendations_discount_code_click"
    const val EVENT_RECOMMENDATIONS_VARIANT_EXPOSURE = "recommendations_variant_exposure"
    const val EVENT_FEEDBACK_UPDATE = "feedback_update"
    const val EVENT_PROFILE_QUIZ_CLICK = "profile_quiz_click"
    const val EVENT_PROFILE_QUIZ_NEW = "profile_quiz_new"
    const val EVENT_LOG_OUT = "log_out"
    const val EVENT_BEGIN_CHECKOUT = "begin_checkout"

    // Parameter keys
    const val PARAM_CTA_LOCATION = "cta_location"
    const val PARAM_FAQ_ID = "faq_id"
    const val PARAM_TEMPLATE_ID = "quiz_template_id"
    const val PARAM_QUESTION_ID = "question_id"
    const val PARAM_TIME_SPENT_S = "time_spent_s"
    const val PARAM_ACADEMIC_YEAR = "academic_year"
    const val PARAM_PRICE_VARIANT = "price_variant"
    const val PARAM_RECOMMENDATIONS_VARIANT = "recommendations_variant"
    const val PARAM_METHOD = "method"

    // User property keys
    const val USER_PROP_ACADEMIC_YEAR = "academic_year"
    const val USER_PROP_PRICE_VARIANT = "price_variant"
    const val USER_PROP_RECOMMENDATIONS_VARIANT = "recommendations_variant"

    // CTA location values
    const val CTA_LOCATION_MENU = "menu"
    const val CTA_LOCATION_TOP = "top"
    const val CTA_LOCATION_BOTTOM = "bottom"

    // Method values
    const val METHOD_EMAIL = "email"
    const val METHOD_GOOGLE = "google"

    /**
     * Logs an event to the analytics service.
     *
     * @param name The name of the event to log.
     * @param params The parameters to log with the event.
     */
    fun logEvent(name: String, params: Map<String, Any>? = null) {
        val analytics = getAnalytics()

        val jsonParams = json()
        params?.entries?.forEach { pair ->
            jsonParams[pair.key] = pair.value
        }

        logEvent(analytics, name, jsonParams)
    }

    fun logErrorDisplayedEvent(
        location: String,
        errorType: String,
        quizId: String? = null,
        params: Map<String, Any> = emptyMap()
    ) {
        val mergedParams: MutableMap<String, Any> = params.toMutableMap()
        mergedParams["location"] = location
        mergedParams["error_type"] = errorType
        quizId?.let { mergedParams["quiz_id"] = it }

        logEvent(EVENT_ERROR_DISPLAYED, mergedParams)
    }

    /**
     * Sets a user property in analytics.
     *
     * @param propertyName The name of the user property.
     * @param propertyValue The value of the user property.
     */
    fun setUserProperty(propertyName: String, propertyValue: String) {
        val analytics = getAnalytics()

        val jsonProperty = json()
        jsonProperty[propertyName] = propertyValue

        setUserProperties(analytics, jsonProperty)
    }
}

/**
 * Emit purchase analytics event with GA4 ecommerce format
 */
fun emitPurchaseEvent(
    quizId: String,
    paymentId: String?,
    amountMinorUnits: Long?,
    currencyCode: String?,
) {
    val amountFromPayment: Double? = amountMinorUnits?.let { it.toDouble() / 100.0 }
    val amount: Double = amountFromPayment ?: 0.0
    val currency: String = currencyCode?.uppercase() ?: "RON"

    // Get user context for analytics (access UserRepository statically)
    val academicYear = try {
        info.rezident.app.data.user.UserRepository.user.value?.academicYear ?: "unknown"
    } catch (e: Exception) {
        "unknown"
    }
    val priceVariant = try {
        info.rezident.app.data.user.UserRepository.user.value?.priceVariant?.name ?: "unknown"
    } catch (e: Exception) {
        "unknown"
    }
    val recommendationsVariant =
        info.rezident.app.data.user.VariantsRepository.recommendationsVariantLabelOrNull() ?: "unknown"

    AppTracking.logEvent(
        "purchase",
        mapOf(
            "transaction_id" to (paymentId ?: ""),
            "value" to amount,
            "currency" to currency,
            AppTracking.PARAM_ACADEMIC_YEAR to academicYear,
            AppTracking.PARAM_PRICE_VARIANT to priceVariant,
            AppTracking.PARAM_RECOMMENDATIONS_VARIANT to recommendationsVariant,
            "items" to mapOf(
                "item_id" to "1",
                "item_name" to "Recomandări Complete"
            )
        )
    )

    Logger.log("Purchase event emitted for quiz $quizId with transaction $paymentId")
}